import type {
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import type { MapWorkspace } from '@trimble/geospatial';
import type { ProjectDataRepository } from '@trimble/project-data-repository';

import {
  MISSION_ROOT_NODE_ID,
  MODEL_ROOT_NODE_ID,
  POINT_CLOUD_ROOT_NODE_ID,
  TRAJECTORY_ROOT_NODE_ID,
} from './project-data-tree-nodes';
import {
  MAP_WORKSPACES_ROOT_NODE_ID,
  makeWorkspaceLayerNodeId,
} from './workspace-tree-nodes';

export type FxWorkspaceFolderDescendantRefTrnsGetter = (
  nodeId: string
) => readonly string[];

export function createMissionFolderDescendantRefTrnsGetter(
  missions: readonly MissionItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  // A mission maps to its trajectory. The selection covers only the path
  // (alignment trn), never the mission trn or the run ids. A mission without a
  // path has nothing to select.
  const missionRefTrns = (mission: MissionItem): string[] =>
    mission.pathId == null ? [] : [mission.pathId];

  const refTrnsByMissionId = new Map(
    missions.map((mission) => [mission.id, missionRefTrns(mission)])
  );

  return (nodeId: string): readonly string[] => {
    if (nodeId === MISSION_ROOT_NODE_ID) {
      return missions.flatMap(missionRefTrns);
    }

    return refTrnsByMissionId.get(nodeId) ?? [];
  };
}

export function createMissionRemovableRunRefTrnsGetter(
  missions: readonly MissionItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  // Older workspaces stored each run id. Run rows no longer have a checkbox,
  // so the mission checkbox and the missions root must be able to remove them.
  const runIdsByMissionId = new Map(
    missions.map((mission) => [mission.id, mission.runs.map((run) => run.id)])
  );
  const allRunIds = [...runIdsByMissionId.values()].flat();

  return (nodeId: string): readonly string[] => {
    if (nodeId === MISSION_ROOT_NODE_ID) {
      return allRunIds;
    }

    return runIdsByMissionId.get(nodeId) ?? [];
  };
}

export function collectModelFolderDescendantRefTrns(
  repository: ProjectDataRepository,
  nodeId: string
): readonly string[] {
  const storeParentId =
    nodeId === MODEL_ROOT_NODE_ID ? repository.rootId : nodeId;
  const refTrns: string[] = [];

  const visit = (parentId: string): void => {
    for (const childId of repository.getChildren(parentId)) {
      const child = repository.getNode(childId);
      if (child === undefined || child.isDeleted) {
        continue;
      }

      if (child.type === 'FOLDER') {
        visit(childId);
        continue;
      }

      refTrns.push(childId);
    }
  };

  visit(storeParentId);
  return refTrns;
}

export function createPointCloudFolderDescendantRefTrnsGetter(
  items: readonly PointCloudItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const standaloneRefTrns = items
    .filter((item) => item.mission === undefined)
    .map((item) => item.id);

  return (nodeId: string): readonly string[] =>
    nodeId === POINT_CLOUD_ROOT_NODE_ID ? standaloneRefTrns : [];
}

export function createTrajectoryFolderDescendantRefTrnsGetter(
  items: readonly TrajectoryItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const standaloneRefTrns = items
    .filter((item) => item.mission === undefined)
    .map((item) => item.id);

  return (nodeId: string): readonly string[] =>
    nodeId === TRAJECTORY_ROOT_NODE_ID ? standaloneRefTrns : [];
}

export function createMapWorkspaceFolderDescendantRefTrnsGetter(
  workspaces: readonly MapWorkspace[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const layersByWorkspaceId = new Map(
    workspaces.map((workspace) => [
      workspace.id,
      workspace.layerIds.map((layerId) =>
        makeWorkspaceLayerNodeId(workspace.id, layerId)
      ),
    ])
  );

  return (nodeId: string): readonly string[] => {
    if (nodeId === MAP_WORKSPACES_ROOT_NODE_ID) {
      return workspaces.flatMap(
        (workspace) => layersByWorkspaceId.get(workspace.id) ?? []
      );
    }

    return layersByWorkspaceId.get(nodeId) ?? [];
  };
}
